import { cx } from '@flowus/common/cx';
import { getFormatUrl } from '@flowus/common/url';
import { last } from 'lodash-es';
import type { FC } from 'react';
import { useCallback, useEffect, useState } from 'react';
import { Button } from 'src/common/components/button';
import { Input } from 'src/common/components/input';
import { LoadingContainer } from 'src/common/components/loading-container';
import { message } from 'src/common/components/message';
import type { ModalSchema } from 'src/common/components/next-modal';
import { useOpenModal } from 'src/common/components/next-modal';
import { PUBLIC_SHARE } from 'src/common/const';
import { updateViewFormat } from 'src/hooks/block/use-update-collection-view';
import { useCollectionView } from 'src/hooks/collection-view/use-collection-view';
import { useFetchPage } from 'src/hooks/page';
import { getPermissions } from 'src/hooks/share/use-permissions';
import { ViewPath } from 'src/utils';
import { Deferred } from 'src/utils/async-utils';
import { bizTracker } from 'src/utils/biz-tracker';
import { isFormLink, isShareLink } from 'src/utils/getPageId';
import { usePickBlock } from 'src/utils/pick-block';
import { HeaderCover } from 'src/views/main/page-doc/header-cover';
import { FormContainer } from '../form-container';
import { PreviewEndPage } from './preview-end-page';
import { useAppTranslation } from '@flowus/common/i18n/use-translation';

interface EditorFormEndPageProps extends ModalSchema.CloseModal {
  collectionId: string;
  viewId: string;
}
export const EditorFormEndPage: FC<EditorFormEndPageProps> = (props) => {
  const { collectionId, viewId, onCloseModal } = props;
  const { t } = useAppTranslation();
  const view = useCollectionView(viewId);
  const fetchPage = useFetchPage();
  const block = usePickBlock(collectionId, ['data'], ['cover', 'coverPos']);
  const [link, setLink] = useState(view?.format.formEndPage ?? '');
  const [previewId, setPreviewId] = useState('');

  const [loading, setLoading] = useState(true);

  const checkUrl = async () => {
    const deferred = new Deferred<string>();
    // 校验链接，并跳转到下一步预览
    const url = getFormatUrl(link);
    const id = last(url?.pathname.split(ViewPath.share))?.slice(0, 36);
    if (isFormLink(link)) {
      message.error(t('bitable.form.form_link_not_supported'));
      return;
    }
    if (!isShareLink(link) || !id) {
      message.error(t('bitable.form.invalid_share_link'));
      return;
    }
    // 链接未开启分享
    await fetchPage(id, {
      callback: (res) => {
        if (res.code === 200) {
          if (getPermissions(id).shared) {
            deferred.resolve(id);
            return;
          }
        }
        message.error(t('bitable.form.share_not_enabled'));
        deferred.resolve('');
      },
    }).catch(() => {
      message.error(t('bitable.form.share_not_enabled'));
      deferred.resolve('');
    });
    return deferred.promise;
  };

  const submitLink = async () => {
    if (!link) return;
    const id = await checkUrl();
    if (id) {
      setPreviewId(id);
    }
  };

  const saveLink = async () => {
    if (link) {
      const id = await checkUrl();
      if (id) {
        bizTracker.event('end_page');
        updateViewFormat(viewId, { formEndPage: link });
      }
    } else {
      updateViewFormat(viewId, { formEndPage: link });
    }

    onCloseModal();
  };

  const resetEndPage = () => {
    if (view?.format.formEndPage) {
      updateViewFormat(viewId, { formEndPage: undefined });
      message.success(t('common.set_success'));
    }
    setPreviewId('');
    setLink('');
  };

  useEffect(() => {
    if (view?.format.formEndPage) {
      void submitLink().then(() => {
        setLoading(false);
      });
    } else {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!block) return null;
  if (loading) return <LoadingContainer />;

  const settingLink = (
    <div className="pt-14 px-24 mx-auto h-full flex flex-col text-left">
      <div className="text-h3">{t('bitable.form.end_page_embed_desc')}</div>
      <div className="mt-10 text-t2">{`1. ${t('bitable.form.write_content')}  2. ${t(
        'bitable.form.enable_sharing'
      )}${PUBLIC_SHARE}${t('bitable.form.and_paste')}`}</div>
      <Input
        autoFocus
        value={link}
        onChange={setLink}
        className="h-8 w-full mt-7"
        placeholder={t('bitable.form.paste_share_link')}
        addonAfter={
          <div
            onClick={submitLink}
            className={cx(
              'w-24 h-full flex items-center justify-center border-l bg-white2 rounded-l-none',
              !link && 'cursor-not-allowed',
              link && 'animate-hover animate-click'
            )}
          >
            {t('common.preview')}
          </div>
        }
      />
    </div>
  );

  return (
    <div className="flex flex-col h-full relative">
      {/* Header */}
      <div className="w-full flex items-center justify-between h-14 px-4 text-t1-medium bg-white2 flex-shrink-0 sticky top-0 left-0 z-20">
        <div className="text-t2-medium">{t('common.end_page_design')}</div>
        <div className="flex">
          <Button colorType="base_white" className="mr-2" onClick={() => onCloseModal()}>
            {t('common.cancel')}
          </Button>
          <Button colorType="active" onClick={saveLink}>
            {t('common.done')}
          </Button>
        </div>
      </div>
      <div className="flex absolute top-16 right-4 z-50">
        {link && (
          <Button colorType="base_white" className="mr-4" onClick={resetEndPage}>
            {t('bitable.form.restore_default')}
          </Button>
        )}
        {previewId && (
          <Button colorType="base_white" onClick={() => setPreviewId('')}>
            {t('bitable.form.modify_link')}
          </Button>
        )}
      </div>
      <FormContainer
        className={'h-[60vh] flex px-0'}
        containerClassName={'pb-20'}
        header={
          <HeaderCover
            uuid={block.uuid}
            ossName={block.data.cover || 'cover/flowus2'}
            hiddenEditorButton
            height={320}
          />
        }
        hiddenFooter
      >
        {previewId ? <PreviewEndPage uuid={previewId} /> : settingLink}
      </FormContainer>
    </div>
  );
};

export const useOpenFormEndPageSetting = () => {
  const openModal = useOpenModal();

  const openFormEndPageSetting = (params: { collectionId: string; viewId: string }) => {
    openModal.modal({
      className: 'w-[95vw] h-[95vh]',
      content: (_) => (
        <EditorFormEndPage
          viewId={params.viewId}
          collectionId={params.collectionId}
          onCloseModal={_.onCloseModal}
        />
      ),
    });
  };
  return useCallback(openFormEndPageSetting, [openModal]);
};
